// inv/src/pages/FindInvestors.jsx
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import InvestorsFilter from '../components/find-investors/modules/investorsFilter';
import InvestorsData from '../components/find-investors/modules/investorsData';
import { API_URL } from '../config';
import languages from '../locales/languages';
import { useInitialData } from '../components/data/DataContext';
import SeoHead from '../seo/seoHead';
import { useInvestorsSearch } from '../hooks/useInvestorsSearch';
import { gaTrack } from '../service-scripts/gaTrack';

const ITEMS_PER_PAGE = 10;

const FindInvestors = () => {
  const location = useLocation();
  const ssr = useInitialData() || {};
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const ssrLang = ssr.lang || null;
  const firstSeg = location.pathname.split('/').filter(Boolean)[0];
  const urlLang = languages.some(l => l.code === firstSeg) ? firstSeg : 'en';
  const lng = urlLang || ssrLang || 'en';

  const ssrInvestors = ssr.investors || [];
  const ssrTotalResults = ssr.totalResults || 0;
  const hasSsrInvestorsResponse = ssr.investorsFetchOk === true;
  const ssrPage = ssr.page || 1;
  const ssrFilters = ssr.filters || {};
  const seo = ssr.seo || {};

  const initialFilters = useMemo(
    () => ({
      mainOfficeLocation: searchParams.get('country') || ssrFilters.country || '',
      industry: searchParams.get('industries') || ssrFilters.industries || '',
      keyword: searchParams.get('keyword') || ssrFilters.keyword || '',
      selectedTypes:
        searchParams.get('types') || (ssrFilters.types || []).join(',')
          ? (searchParams.get('types') || (ssrFilters.types || []).join(','))
              .split(',')
              .filter(Boolean)
          : [],
    }),
    [searchParams, ssrFilters.country, ssrFilters.industries, ssrFilters.keyword, ssrFilters.types]
  );

  const buildRequestParams = useCallback(({ page, pageSize, filters: activeFilters }) => {
    const params = { page, limit: pageSize };

    if (activeFilters.mainOfficeLocation) params.country = activeFilters.mainOfficeLocation;
    if (activeFilters.industry) params.industries = activeFilters.industry;
    if (activeFilters.keyword) params.name = activeFilters.keyword;
    if (activeFilters.selectedTypes.length) params.types = activeFilters.selectedTypes.join(',');

    return params;
  }, []);

  const mapResponse = useCallback((data) => ({
    items: data?.investors || [],
    totalResults: data?.totalCount || 0,
    meta: {
      previewCap: data?.publicPreview?.active
        ? data?.publicPreview?.cap ?? data?.visibleCount ?? null
        : null,
      pagingLocked: Boolean(data?.publicPreview?.lockPaging),
    },
  }), []);

  const shouldSkipInitialFetch = useCallback(({ page, filters: activeFilters }) => (
    ssrPage === page &&
    ((ssr.filters?.country) || '') === (activeFilters.mainOfficeLocation || '') &&
    ((ssr.filters?.industries) || '') === (activeFilters.industry || '') &&
    ((ssr.filters?.keyword) || '') === (activeFilters.keyword || '') &&
    (Array.isArray(ssr.filters?.types) ? ssr.filters.types.join(',') : '') ===
      activeFilters.selectedTypes.join(',')
  ), [ssr.filters, ssrPage]);

  const {
    filters,
    items: investors,
    totalResults,
    isLoading,
    currentPage,
    setPage,
    setKeyword,
    setMainOfficeLocation,
    setIndustry,
    setSelectedTypes,
    meta,
  } = useInvestorsSearch({
    endpoint: `${API_URL}/investors-limited`,
    pageSize: ITEMS_PER_PAGE,
    initialPage: ssrInvestors.length ? ssrPage : Number(searchParams.get('page') || 1),
    initialFilters,
    initialItems: ssrInvestors,
    initialTotalResults: ssrTotalResults,
    hasInitialResponse: hasSsrInvestorsResponse,
    fetchDebounceMs: 500,
    pageParam: 'page',
    syncPageToUrl: true,
    replaceHistory: true,
    buildRequestParams,
    mapResponse,
    shouldSkipInitialFetch,
  });

  const previewCap = meta?.previewCap ?? null;
  const pagingLocked = Boolean(meta?.pagingLocked);

  useEffect(() => {
    gaTrack('view_find_investors', {
      context: 'investors',
      has_url_country: !!initialFilters.mainOfficeLocation,
      has_url_industry: !!initialFilters.industry,
      has_url_keyword: !!initialFilters.keyword,
      url_types_count: initialFilters.selectedTypes.length,
    });
  }, [
    initialFilters.mainOfficeLocation,
    initialFilters.industry,
    initialFilters.keyword,
    initialFilters.selectedTypes.length,
  ]);

  return (
    <div className="my-8">
      {/* SEO & SSR */}
      {seo && (
        <SeoHead
          lang={lng}
          title={seo.title}
          description={seo.description}
          canonicalUrl={seo.canonicalUrl}
          ogTitle={seo.ogTitle}
          ogDescription={seo.ogDescription}
          ogImage={seo.ogImage}
          ogUrl={seo.ogUrl}
          ogType={seo.ogType}
          twitterCard={seo.twitterCard}
          alternates={seo.alternates}
          jsonLd={seo.jsonLd}
        />
      )}

      <div className="flex flex-col lg:flex-row">
        <div className="w-full lg:w-1/4 px-12 md:px-12 lg:px-18 rounded-md mt-4">
          <InvestorsFilter
            onKeywordChange={setKeyword}
            onLocationChange={setMainOfficeLocation}
            onUpdateTypes={setSelectedTypes}
            onIndustriesChange={setIndustry}
            totalResults={totalResults}
          />
        </div>

        <div className="w-full px-12 lg:w-3/4 lg:pl-8 lg:pr-12">
          <InvestorsData
            lng={lng}
            investors={investors}
            searchKeyword={filters.keyword}
            mainOfficeLocation={filters.mainOfficeLocation}
            selectedTypes={filters.selectedTypes}
            industries={filters.industry}
            currentPage={currentPage}
            onPageChange={setPage}
            totalResults={totalResults}
            isLoading={isLoading}
            pageSize={ITEMS_PER_PAGE}
            previewCap={previewCap}
            pagingLocked={pagingLocked}
          />
        </div>
      </div>
    </div>
  );
};

export default FindInvestors;
